export default `<!DOCTYPE html>
<html lang="en">
<!--
  ====================================================================
  Scantrust DPP — BATTERY (EU 2023/1542) landing-page TEMPLATE
  Design: Scantrust Enterprise Design System (primary #15BBF0, Roboto)
  Language: English (en)
  Regulatory basis: EU 2023/1542 (Battery Regulation), Annex XIII +
                    ESPR 2024/1781 + PPWR 2025/40
  --------------------------------------------------------------------
  Two kinds of dynamic content:

  1) STC variables (single braces) — auto-filled by Scantrust at scan:
       {product_name} {product_description} {product_image} {product_url}
       {product_sku} {brand_name} {extended_id} {serial_number}
       {scan_count} {scan_id} {country}
     Do NOT rename these; the renderer substitutes them.

  2) Battery DPP fields — plain editable values (NOT variables) so they
     publish correctly and can be edited per product/serial. The
     Scantrust renderer does NOT substitute these — only {single-brace}
     variables. Replace the example values (Voltura PowerCell PC-72) with
     real data per battery unit.

  Applies to: EV / LMT / Industrial >2 kWh. Some fields apply only to
  certain categories — see the EU Battery Regulation Annex XIII Part C
  for the full applicability matrix.

  Access tiers shown via lock chips:
    [Consumer]  — always visible
    [Repairer]  — informational only on consumer landing
    [Authorities] — informational only on consumer landing
  ====================================================================
-->
<head>
  <meta charset="UTF-8" />
  <meta http-equiv="Content-Language" content="en" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Battery Passport — Digital Product Passport</title>
  <style>
    /* Scantrust Enterprise Design System — applied tokens */
    :root {
      --v-primary: #15BBF0;
      --v-secondary: #1F5FAC;
      --v-accent: #5AD2F8;
      --v-success: #3FC135;
      --v-warning: #FF7F00;
      --v-error: #F23C2F;

      --cyan-wash: #E0F4FD;
      --cyan-3: #B0E2F9;
      --deep-blue: #0789B2;

      --fg-1: #222222;
      --fg-2: #333333;
      --fg-3: #555555;
      --fg-muted: #767676;
      --text-primary: rgba(0,0,0,0.87);
      --text-secondary: rgba(0,0,0,0.60);

      --surface-app: #FAFAFA;
      --surface-card: #FFFFFF;
      --surface-panel: #F1F1F1;
      --border-1: #DADADA;
      --divider: #EDEDED;
      --dot-grey: #D9D9D9;

      --radius-md: 4px;
      --radius-pill: 9999px;

      --elev-2: 0 3px 1px -2px rgba(0,0,0,.2), 0 2px 2px 0 rgba(0,0,0,.14), 0 1px 5px 0 rgba(0,0,0,.12);
      --elev-4: 0 2px 4px -1px rgba(0,0,0,.2), 0 4px 5px 0 rgba(0,0,0,.14), 0 1px 10px 0 rgba(0,0,0,.12);

      --font-sans: 'Roboto', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif;
      --font-mono: 'Roboto Mono', ui-monospace, SFMono-Regular, Menlo, monospace;

      --s-2: 8px; --s-3: 12px; --s-4: 16px; --s-5: 20px; --s-6: 24px;
    }

    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: var(--surface-app); color: var(--fg-3); font-family: var(--font-sans); font-size: 14px; line-height: 20px; -webkit-font-smoothing: antialiased; }
    .wrap { max-width: 480px; margin: 0 auto; padding-bottom: 40px; }
    .inner { padding: 0 var(--s-4); }

    .v-app-bar { background: var(--surface-card); height: 56px; padding: 0 var(--s-4); display: flex; align-items: center; border-bottom: 1px solid var(--divider); box-shadow: var(--elev-4); position: sticky; top: 0; z-index: 5; }
    .v-app-bar .logo { height: 20px; width: auto; display: block; }

    .titleblock { padding: var(--s-5) var(--s-4) var(--s-4); }
    .titleblock .kicker { font-family: var(--font-mono); font-size: 11px; letter-spacing: .08em; text-transform: uppercase; color: var(--text-secondary); margin: 0 0 var(--s-2); }
    .titleblock h1 { font-family: var(--font-sans); font-weight: 700; font-size: 24px; line-height: 30px; letter-spacing: .7px; color: #323232; margin: 0; }
    .titleblock .by { margin: 4px 0 0; font-size: 14px; color: var(--text-secondary); }

    .v-card { background: var(--surface-card); border-radius: var(--radius-md); box-shadow: var(--elev-2); margin-bottom: var(--s-3); overflow: hidden; }
    .v-card.outlined { box-shadow: none; border: 1px solid var(--divider); }

    .hero-img { width: 100%; display: block; aspect-ratio: 4 / 3; object-fit: cover; background: var(--cyan-wash); border: none; }
    .hero-body { padding: var(--s-4); }
    .hero-body .v-card__title { font-family: var(--font-sans); font-weight: 500; font-size: 18px; line-height: 24px; letter-spacing: .2px; color: var(--fg-1); margin: 0 0 4px; }
    .hero-body .v-card__text { font-size: 14px; line-height: 20px; color: var(--fg-3); margin: 0; }

    .panel { list-style: none; }
    .panel > summary { list-style: none; cursor: pointer; padding: var(--s-4); display: flex; align-items: center; justify-content: space-between; gap: var(--s-3); }
    .panel > summary::-webkit-details-marker { display: none; }
    .panel > summary .titlewrap { display: flex; align-items: center; gap: var(--s-3); }
    .panel > summary .ico { flex: 0 0 auto; width: 32px; height: 32px; border-radius: var(--radius-md); background: var(--cyan-wash); color: var(--v-primary); display: flex; align-items: center; justify-content: center; }
    .panel > summary .ico svg { width: 18px; height: 18px; }
    .panel > summary .htxt { font-family: var(--font-sans); font-weight: 500; font-size: 16px; letter-spacing: .2px; color: var(--fg-1); }
    .panel > summary .chev { flex: 0 0 auto; width: 20px; height: 20px; color: var(--text-secondary); transition: transform .2s ease; }
    .panel[open] > summary .chev { transform: rotate(180deg); }
    .panel-body { padding: 0 var(--s-4) var(--s-4); border-top: 1px solid var(--divider); }
    .panel-body .intro { margin: var(--s-3) 0 var(--s-2); font-size: 13px; color: var(--text-secondary); }

    .access-strip { display: flex; align-items: center; gap: 6px; margin-top: 4px; font-size: 11px; letter-spacing: .04em; text-transform: uppercase; color: var(--text-secondary); }
    .access-strip svg { width: 12px; height: 12px; }

    .spec { display: flex; justify-content: space-between; gap: var(--s-4); padding: 10px 0; border-bottom: 1px solid var(--divider); font-size: 14px; }
    .spec:last-child { border-bottom: none; }
    .spec .k { color: var(--text-secondary); flex: 1 1 auto; }
    .spec .v { font-weight: 500; color: var(--fg-2); text-align: right; flex: 0 0 auto; max-width: 58%; }
    .spec .v.mono { font-family: var(--font-mono); font-weight: 400; font-size: 13px; }

    .stat { display: inline-flex; align-items: center; gap: 6px; }
    .stat .d { width: 9px; height: 9px; border-radius: 50%; }

    .fibre { margin: var(--s-3) 0 4px; }
    .fibre-head { display: flex; justify-content: space-between; font-size: 14px; margin-bottom: 5px; color: var(--fg-2); }
    .fibre-head .pct { font-weight: 700; }
    .bar { height: 8px; border-radius: var(--radius-pill); background: var(--cyan-wash); overflow: hidden; }
    .bar > span { display: block; height: 100%; background: var(--v-primary); border-radius: var(--radius-pill); }

    .chips { display: flex; flex-wrap: wrap; gap: var(--s-2); margin-top: var(--s-3); }
    .v-chip { display: inline-flex; align-items: center; height: 28px; padding: 0 12px; border-radius: var(--radius-pill); background: var(--surface-panel); color: var(--fg-2); font-size: 13px; font-weight: 400; }
    .v-chip.primary { background: var(--cyan-wash); color: var(--deep-blue); }
    .v-chip.success { background: #E6F8E5; color: #2A8B22; }
    .v-chip.warning { background: #FFF1DF; color: #8a5a12; }
    .v-chip.restricted { background: #F1F1F1; color: var(--fg-muted); }
    .v-chip svg { width: 13px; height: 13px; margin-right: 6px; }

    .chain { margin: var(--s-3) 0 2px; padding: 0; list-style: none; }
    .chain li { position: relative; padding: 0 0 var(--s-4) 26px; font-size: 14px; }
    .chain li:last-child { padding-bottom: 0; }
    .chain li::before { content: ""; position: absolute; left: 4px; top: 4px; width: 10px; height: 10px; border-radius: 50%; background: var(--v-primary); }
    .chain li::after { content: ""; position: absolute; left: 8px; top: 15px; bottom: -2px; width: 2px; background: var(--cyan-3); }
    .chain li:last-child::after { display: none; }
    .chain .stage { font-weight: 500; color: var(--fg-2); }
    .chain .place { color: var(--text-secondary); font-size: 13px; }

    .metrics { display: grid; grid-template-columns: 1fr 1fr; gap: var(--s-2); margin-top: var(--s-3); }
    .metric { background: var(--cyan-wash); border-radius: var(--radius-md); padding: var(--s-4); text-align: center; }
    .metric .num { font-size: 22px; font-weight: 700; color: var(--deep-blue); line-height: 1.1; }
    .metric .unit { font-size: 12px; color: var(--text-secondary); font-weight: 500; }
    .metric .cap { font-size: 12px; color: var(--text-secondary); margin-top: 4px; }

    .class-pill { display: inline-flex; align-items: center; justify-content: center; min-width: 28px; height: 28px; border-radius: var(--radius-md); background: var(--v-primary); color: #fff; font-weight: 700; font-size: 14px; padding: 0 8px; }
    .class-scale { display: inline-flex; gap: 3px; margin-left: 8px; }
    .class-scale span { width: 14px; height: 6px; background: var(--surface-panel); border-radius: 2px; }
    .class-scale span.on { background: var(--v-primary); }

    .v-btn { display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%; height: 44px; margin: var(--s-2) 0 4px; background: var(--v-primary); color: #FFFFFF; border: none; border-radius: var(--radius-md); font-family: var(--font-sans); font-weight: 500; font-size: 14px; letter-spacing: 0.0892857em; text-transform: uppercase; text-decoration: none; box-shadow: var(--elev-2); cursor: pointer; }

    .scanmeta { margin: var(--s-4) 0 4px; font-size: 12px; color: var(--text-secondary); text-align: center; line-height: 1.7; }
    .scanmeta code { font-family: var(--font-mono); font-size: 12px; color: var(--fg-2); background: var(--surface-panel); padding: 1px 6px; border-radius: var(--radius-md); }

    .stc-powered-by { text-align: center; padding: var(--s-6) 0 4px; font-size: 12px; color: var(--text-secondary); }
    .stc-powered-by a { color: var(--v-primary); text-decoration: none; font-weight: 500; }
    .stc-powered-by .sep { margin: 0 var(--s-2); opacity: 0.5; }
  </style>
</head>
<body>
  <div class="wrap">

    <header class="v-app-bar">
      <svg class="logo" viewBox="0 0 688 96" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Scantrust">
        <g transform="translate(-56,-84)">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M378.698 156.843V117.73H359.758V103.459H379.282V84H395.184V103.459H422.068V117.73H395.184V164.432H422.068V178.703H400.675C384.773 178.703 378.698 169.816 378.698 156.843ZM56 154.054H72.8755C72.8755 161.488 78.1588 165.717 87.5182 165.717C96.8646 165.717 101.758 162.512 101.758 157.258C101.758 153.029 98.7339 149.968 91.9707 148.657L78.8728 146.179C66.7873 143.844 58.726 136.852 58.726 124.022C58.739 111.632 69.2408 102.162 87.0898 102.162C104.939 102.162 117.011 112.229 117.011 126.811H101.434C101.148 119.961 95.4237 115.563 86.934 115.563C79.4569 115.563 74.7058 118.625 74.7058 124.164C74.7058 128.679 77.8732 131.157 84.0652 132.324L96.7348 134.802C109.677 137.293 118.309 142.975 118.309 157.258C118.309 171.84 104.783 180 87.5182 180C67.5143 180 56.4284 169.946 56 154.054ZM586.928 154.054H603.804C603.804 161.488 609.087 165.717 618.446 165.717C627.793 165.717 632.687 162.512 632.687 157.258C632.687 153.029 629.662 149.968 622.899 148.657L609.801 146.179C597.716 143.844 589.654 136.852 589.654 124.022C589.654 111.632 600.156 102.162 618.005 102.162C635.854 102.162 647.94 112.229 647.94 126.811H632.362C632.077 119.961 626.352 115.563 617.862 115.563C610.385 115.563 605.634 118.625 605.634 124.164C605.634 128.679 608.801 131.157 614.993 132.324L627.663 134.802C640.605 137.28 649.251 142.962 649.251 157.245C649.238 171.84 635.711 180 618.446 180C598.443 180 587.357 169.946 586.928 154.054ZM236.762 134.595C216.408 134.595 207.879 143.222 207.879 156.532C207.879 169.803 216.252 180 232.959 180C243.071 180 250.678 176.212 255.079 169.803H256.948L258.506 178.703H271.487V128.536C271.487 113.669 260.349 102.162 240.423 102.162C221.38 102.162 210.475 113.15 210.475 128.108H226.053C226.053 121.673 229.986 115.862 239.995 115.862C250.535 115.862 255.909 121.116 255.909 128.108V134.595H236.762ZM255.909 145.622V148.904C255.909 159.243 248.536 166.78 236.619 166.78C227.533 166.78 222.807 162.123 222.807 156C222.807 149.293 226.91 145.622 237.736 145.622H255.909ZM287.064 103.472L300.045 103.459L301.915 113.241H303.797C307.756 106.832 315.142 102.162 326.008 102.162C342.74 102.162 353.268 112.657 353.268 129.561V178.703H336.393V134.257C336.393 124.06 331.46 117.172 320.971 117.172C310.145 117.185 303.94 124.437 303.94 137.189V178.703H287.064V103.472ZM563.562 178.703L576.543 178.69V103.459H559.668V144.986C559.668 157.738 553.463 164.99 542.637 164.99C532.148 164.99 527.215 158.102 527.215 147.905V103.459H510.34V152.601C510.34 169.505 520.867 180 537.6 180C548.465 180 555.852 175.33 559.811 168.921H561.693L563.562 178.703ZM672.072 117.73V156.843C672.072 169.816 678.147 178.703 694.049 178.703H715.442V164.432H688.558V117.73H715.442V103.459H688.558V84H672.656V103.459H653.132V117.73H672.072ZM435.049 164.432H449.666V117.73H435.049V103.459H462.309L463.608 115.135H466.204C469.397 107.377 476.368 102.162 485.805 102.162H496.06V117.73H485.364C472.733 117.73 466.217 126.305 466.217 141.82V164.432H496.06V178.703H435.01L435.049 164.432ZM144.921 141.081C144.921 155.948 151.191 165.859 163.951 165.859C172.532 165.859 178.023 161.241 179.321 154.054H196.196C194.431 169.129 183.955 180 164.25 180C141.169 180 128.694 164.406 128.694 141.081C128.694 117.898 140.676 102.162 163.743 102.162C183.864 102.162 193.6 112.54 196.196 128.108H179.321C177.828 120.999 172.194 116.432 163.743 116.432C150.983 116.432 144.921 126.499 144.921 141.081Z" fill="#222222"/>
          <path fill-rule="evenodd" clip-rule="evenodd" d="M729.721 103.459H744V117.73H729.721V103.459ZM729.721 164.433H744V178.703H729.721V164.433Z" fill="#15BBF0"/>
        </g>
      </svg>
    </header>

    <div class="titleblock">
      <p class="kicker">Battery Passport · EU 2023/1542</p>
      <h1>{product_name}</h1>
      <p class="by">by {brand_name}</p>
    </div>

    <div class="inner">

      <!-- Hero -->
      <section class="v-card">
        <img class="hero-img" src="{product_image}" alt="{product_name}" />
        <div class="hero-body">
          <div class="v-card__title">{product_name}</div>
          <p class="v-card__text">{product_description}</p>
        </div>
      </section>

      <!-- Product Identity -->
      <details class="v-card panel" open>
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M7 9h.01M7 13h.01M11 9h6M11 13h6"/></svg></span>
            <span class="htxt">Product Identity</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Unique identifiers and lifecycle status of this battery (EU 2023/1542 Annex XIII §1).</p>
          <div class="spec"><span class="k">Battery Passport ID</span><span class="v mono">{extended_id}</span></div>
          <div class="spec"><span class="k">Serial number</span><span class="v mono">{serial_number}</span></div>
          <div class="spec"><span class="k">Battery category</span><span class="v">Electric vehicle (EV)</span></div>
          <div class="spec"><span class="k">Battery mass</span><span class="v">412 kg</span></div>
          <div class="spec"><span class="k">Manufacturing date</span><span class="v">14 May 2026</span></div>
          <div class="spec"><span class="k">Put into service</span><span class="v">1 Jun 2026</span></div>
          <div class="spec"><span class="k">UPI (ISO/IEC 15459)</span><span class="v mono">0E2C-5A11-PC72-2026</span></div>
          <div class="spec"><span class="k">GTIN</span><span class="v mono">5901234123457</span></div>
          <div class="spec"><span class="k">TARIC / commodity code</span><span class="v mono">8507 60 00 90</span></div>
          <div class="spec"><span class="k">Data carrier</span><span class="v">QR + NFC (ISO/IEC 15459)</span></div>
          <div class="spec"><span class="k">EU DPP registry</span><span class="v mono">EU-DPP-2026-0099147</span></div>
        </div>
      </details>

      <!-- Compliance & Safety -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 12l2 2 4-4"/><path d="M12 3l7 3v5c0 5-3.5 8-7 9-3.5-1-7-4-7-9V6l7-3z"/></svg></span>
            <span class="htxt">Compliance &amp; Safety</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">EU declaration of conformity, hazard symbols and on-board safety information (Battery Regulation Art. 18).</p>
          <div class="spec"><span class="k">EU Declaration of Conformity</span><span class="v"></span>On file</span></span></div>
          <div class="spec"><span class="k">Cadmium (Cd) symbol</span><span class="v">N/A — Cd &lt; 0.002%</span></div>
          <div class="spec"><span class="k">Lead (Pb) symbol</span><span class="v">N/A — Pb &lt; 0.004%</span></div>
          <div class="spec"><span class="k">CF performance label</span><span class="v">B</span></div>
          <div class="spec"><span class="k">Extinguishing agent</span><span class="v">AVD (Aqueous Vermiculite Dispersion) — never water/foam</span></div>
          <div class="spec"><span class="k">Test reports</span><span class="v">Held by manufacturer · disclosed to MSAs on request</span></div>
          <div class="chips">
            <span class="v-chip primary">CE</span>
            <span class="v-chip primary">UN 38.3</span>
            <span class="v-chip primary">IEC 62660</span>
            <span class="v-chip primary">ISO 6469</span>
          </div>
        </div>
      </details>

      <!-- Manufacturer & EU Operators -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21h18M5 21V8l5-3v3l5-3v16M19 21V11l-4-2"/></svg></span>
            <span class="htxt">Manufacturer &amp; EU Operators</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Manufacturer, importer and EU-authorized representative responsible for this passport.</p>
          <div class="spec"><span class="k">Manufacturer</span><span class="v">{brand_name}</span></div>
          <div class="spec"><span class="k">Registered address</span><span class="v">12 Rue Bellecour, 69002 Lyon, France</span></div>
          <div class="spec"><span class="k">Electronic contact</span><span class="v">dpp@voltura-energy.eu</span></div>
          <div class="spec"><span class="k">Operator ID (UOI)</span><span class="v mono">EU-OP-FR-99147</span></div>
          <div class="spec"><span class="k">Manufacturing place</span><span class="v">Lyon, France</span></div>
          <div class="spec"><span class="k">Facility ID (UFI)</span><span class="v mono">FR-LYO-77041</span></div>
          <div class="spec"><span class="k">Importer</span><span class="v">Not applicable — manufactured in EU</span></div>
          <div class="spec"><span class="k">Authorized representative</span><span class="v">Voltura GmbH · Munich, Germany</span></div>
          <div class="spec"><span class="k">AR contact</span><span class="v">ar-eu@voltura-energy.de</span></div>
          <div class="spec"><span class="k">AR Operator ID</span><span class="v mono">EU-OP-DE-44031</span></div>
        </div>
      </details>

      <!-- Materials & Composition -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7l9-4 9 4-9 4-9-4z"/><path d="M3 7v10l9 4 9-4V7"/></svg></span>
            <span class="htxt">Materials &amp; Composition</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </summary>
        <div class="panel-body">   
          <p>
            Cathode(LiFePO₄) (42%), Anode(graphite + Si) (28%), Electrolyte(LiPF₆ / EC-DMC) (12%), Casing, BMS &amp; other (12%).
          </p>
          <div class="spec"><span class="k">Battery chemistry</span><span class="v">LFP — Lithium Iron Phosphate</span></div>
          

          <div class="spec" style="margin-top:12px"><span class="k">Lithium content</span><span class="v">12.3 kg</span></div>
          <div class="spec"><span class="k">Natural graphite</span><span class="v">38.4 kg</span></div>
          <div class="spec"><span class="k">Phosphate</span><span class="v">49.2 kg</span></div>
          <div class="spec"><span class="k">Hazardous substances</span><span class="v">LiPF₆ — see SDS</span></div>
          <div class="spec"><span class="k">Health / environment impact</span><span class="v">Irritant on rupture · non-toxic in use</span></div>
        </div>
      </details>

      <!-- Packaging (PPWR) -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12"/></svg></span>
            <span class="htxt">Packaging</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Packaging composition, recyclability and recycled-content disclosure under EU 2025/40 (PPWR).</p>
          <div class="spec"><span class="k">Material types</span><span class="v">Corrugated cardboard (PAP 21) · EPP foam (PP 05)</span></div>
          <div class="spec"><span class="k">Recyclability class</span><span class="v"><span class="class-pill">B</span><span class="class-scale" aria-hidden="true"><span class="on"></span><span class="on"></span><span></span><span></span><span></span></span></span></div>
          <div class="spec"><span class="k">Recycled content</span><span class="v">65% post-consumer</span></div>
          <div class="spec"><span class="k">Packaging weight</span><span class="v">8.4 kg</span></div>
          <div class="spec"><span class="k">Dimensions (L × W × H)</span><span class="v">1,820 × 940 × 220 mm</span></div>
        </div>
      </details>

      <!-- Sustainability & ESG -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 20A7 7 0 0 1 9.8 6.1C15.5 5 17 4.48 19 2c1 2 2 4.18 2 8a7 7 0 0 1-10 10z"/><path d="M2 21c0-3 1.85-5.36 5.08-6"/></svg></span>
            <span class="htxt">Carbon Footprint &amp; Sustainability</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Lifecycle carbon footprint per kWh by stage, performance class and recycled-content shares (Battery Regulation Art. 7–8).</p>
          <div class="metrics">
            <div class="metric"><div class="num">58<span class="unit"> kg</span></div><div class="cap">CO₂e per kWh — total</div></div>
            <div class="metric"><div class="num">B<span class="unit"></span></div><div class="cap">Performance class</div></div>
          </div>
          <div class="spec" style="margin-top:12px"><span class="k">Raw material &amp; pre-processing</span><span class="v">34 kg CO₂e / kWh</span></div>
          <div class="spec"><span class="k">Manufacturing</span><span class="v">18 kg CO₂e / kWh</span></div>
          <div class="spec"><span class="k">Distribution</span><span class="v">2 kg CO₂e / kWh</span></div>
          <div class="spec"><span class="k">End-of-life &amp; recycling</span><span class="v">4 kg CO₂e / kWh</span></div>
          <div class="spec"><span class="k">Public CF study</span><span class="v">voltura-energy.eu/cf-study-2026</span></div>

          <div class="spec" style="margin-top:8px"><span class="k">Recycled lithium</span><span class="v">22%</span></div>
          <div class="spec"><span class="k">Recycled cobalt</span><span class="v">N/A (LFP — no Co)</span></div>
          <div class="spec"><span class="k">Recycled nickel</span><span class="v">N/A (LFP — no Ni)</span></div>
          <div class="spec"><span class="k">Renewable energy share</span><span class="v">78%</span></div>
          <div class="spec"><span class="k">Commercial warranty</span><span class="v">8 years / 160,000 km</span></div>
          <div class="spec"><span class="k">Expected lifetime</span><span class="v">1,500 cycles @ 100% DoD (IEC 61960-3)</span></div>
        </div>
      </details>

      <!-- Supply Chain & Due Diligence -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18"/></svg></span>
            <span class="htxt">Supply Chain &amp; Due Diligence</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Origin of critical raw materials and due-diligence governance under Battery Regulation Art. 49–52.</p>
          <ul class="chain">
            <li><span class="stage">Lithium</span><br /><span class="place">SQM — Salar de Atacama, Chile · Tianqi — Greenbushes, Australia</span></li>
            <li><span class="stage">Natural graphite</span><br /><span class="place">Syrah Resources — Balama, Mozambique</span></li>
            <li><span class="stage">Phosphate</span><br /><span class="place">OCP Group — Khouribga, Morocco</span></li>
            <li><span class="stage">Cell assembly</span><br /><span class="place">Voltura Gigafactory — Lyon, France</span></li>
          </ul>
          <div class="spec" style="margin-top:12px"><span class="k">Due diligence report (Art. 52)</span><span class="v">voltura-energy.eu/dd-2026.pdf</span></div>
          <div class="spec"><span class="k">3rd-party verification</span><span class="v">TÜV SÜD · valid to 12/2026</span></div>
          <div class="spec"><span class="k">Management system (Art. 49)</span><span class="v">OECD-aligned · ISO 31000</span></div>
          <div class="spec"><span class="k">Risk management plan (Art. 50)</span><span class="v">On file</span></div>
        </div>
      </details>

      <!-- End of Life -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 19H4.8a2 2 0 0 1-1.7-3l1.9-3.2M9.3 4.2l-1.1 1.9M16.7 4.2l2.4 4.1a2 2 0 0 1-1.7 3H14M12 22l3-3-3-3"/></svg></span>
            <span class="htxt">End of Life &amp; Take-back</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">End-user role in waste prevention and separate collection (Battery Regulation Art. 74).</p>
          <div class="spec"><span class="k">Waste prevention</span><span class="v">Do not disassemble · avoid impacts &amp; fire</span></div>
          <div class="spec"><span class="k">Separate collection</span><span class="v">Mandatory — never with household waste</span></div>
          <div class="spec"><span class="k">Collection symbol</span><span class="v">Crossed-out wheeled bin (Annex VI)</span></div>
          <div class="spec"><span class="k">Take-back scheme</span><span class="v">Free at any Voltura dealer or authorized recycler</span></div>
          <div class="spec"><span class="k">Find a collection point</span><span class="v">voltura-energy.eu/recycle</span></div>
          <div class="spec"><span class="k">Preparation for re-use</span><span class="v">Eligible if SoH &gt; 80% — see Repairer access</span></div>
        </div>
      </details>

      <!-- Performance & Repairer information -->
      <details class="v-card panel">
        <summary>
          <span class="titlewrap">
            <span class="ico"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg></span>
            <span class="htxt">Performance &amp; Repair Data</span>
          </span>
          <svg class="chev" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
        </summary>
        <div class="panel-body">
          <p class="intro">Electrical parameters, state-of-health and dismantling guidance for authorized repairers and recyclers.</p>
          <p class="access-strip"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>Restricted — Repairer / Authorities</p>

          <div class="spec" style="margin-top:8px"><span class="k">Rated capacity</span><span class="v">200 Ah</span></div>
          <div class="spec"><span class="k">Nominal voltage</span><span class="v">360 V</span></div>
          <div class="spec"><span class="k">Voltage range</span><span class="v">280 V – 410 V</span></div>
          <div class="spec"><span class="k">Original power</span><span class="v">250 kW</span></div>
          <div class="spec"><span class="k">Capacity exhaustion threshold</span><span class="v">80%</span></div>
          <div class="spec"><span class="k">Operating temperature</span><span class="v">−20 °C to +60 °C</span></div>
          <div class="spec"><span class="k">Round-trip efficiency</span><span class="v">95.4% (initial) · 92.1% @ 50% cycle life</span></div>
          <div class="spec"><span class="k">Internal resistance</span><span class="v">Cell 3.2 mΩ · Pack 56 mΩ</span></div>
          <div class="spec"><span class="k">C-rate of cycle test</span><span class="v">0.5 C</span></div>
          <div class="spec"><span class="k">State of certified energy (SOCE)</span><span class="v">100% at delivery</span></div>
          <div class="spec"><span class="k">State of charge (current)</span><span class="v">92%</span></div>
          <div class="spec"><span class="k">Cycles completed</span><span class="v">12</span></div>
          <div class="spec"><span class="k">Negative events</span><span class="v">None recorded</span></div>
          <div class="spec"><span class="k">Dismantling guide</span><span class="v">voltura-energy.eu/repair/pc72.pdf</span></div>
          <div class="spec"><span class="k">Spare parts contact</span><span class="v">parts@voltura-energy.eu</span></div>
          <div class="chips">
            <span class="v-chip warning">HV — qualified personnel only</span>
            <span class="v-chip">AVD on standby</span>
          </div>
        </div>
      </details>

      <a class="v-btn" href="{product_url}">VIEW FULL PRODUCT DETAILS</a>

      <p class="scanmeta">
        Verified in {country} · scanned {scan_count} times<br />
      </p>

      <footer class="stc-powered-by">
        Powered by Scantrust&#8482;
        <span class="sep">·</span>
        <a href="https://www.scantrust.com/privacy">Privacy Policy</a>
      </footer>

    </div>
  </div>
</body>
</html>`
